import React, { useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import Button from '../../ui/Button';
import CommonModal from '../../ui/CommonModal';
import {
  CommonForm,
  FormActions,
  FormCheckboxField,
  FormField,
  FormGrid,
  FormSection,
  FormSelect,
  formChoiceLabelClassName,
  formChoiceRowClassName,
} from '../../ui/CommonForm';
import Input from '../../ui/Input';
import { CreateDomainRequest, DomainType } from '../../../types';
import { useCreateDomain, useGameProviders } from '../../../hooks/useApi';

interface CreateDomainModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const EMPTY_FORM: CreateDomainRequest = {
  domain: '',
  type: 'kiosk',
  ipWhitelist: null,
  isActive: true,
  label: '',
  cfCnameTarget: '',
  game_ids: [],
};

const CreateDomainModal: React.FC<CreateDomainModalProps> = ({ isOpen, onClose, onSuccess }) => {
  const [form, setForm] = useState<CreateDomainRequest>(EMPTY_FORM);
  const [ipInput, setIpInput] = useState('');
  const [useWhitelist, setUseWhitelist] = useState(false);
  const [showPolicy, setShowPolicy] = useState(false);
  const [showAdvancedNetwork, setShowAdvancedNetwork] = useState(false);
  const [gameMode, setGameMode] = useState<'all' | 'selected'>('all');

  const createDomain = useCreateDomain();
  const { data: gameProvidersData } = useGameProviders();

  if (!isOpen) return null;

  const handleAddIp = () => {
    const ip = ipInput.trim();
    if (!ip) return;
    const list = form.ipWhitelist ?? [];
    if (!list.includes(ip)) {
      setForm({ ...form, ipWhitelist: [...list, ip] });
    }
    setIpInput('');
  };

  const handleRemoveIp = (ip: string) => {
    setForm({ ...form, ipWhitelist: (form.ipWhitelist ?? []).filter(i => i !== ip) });
  };

  const handleToggleWhitelist = (checked: boolean) => {
    setUseWhitelist(checked);
    setForm({ ...form, ipWhitelist: checked ? [] : null });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await createDomain.mutateAsync({
        ...form,
        label: form.label?.trim() || null,
        ipWhitelist: useWhitelist ? (form.ipWhitelist ?? []) : null,
        cfCnameTarget: form.cfCnameTarget?.trim() || null,
        game_ids: gameMode === 'all' ? [] : (form.game_ids ?? []),
      });
      setForm(EMPTY_FORM);
      setIpInput('');
      setUseWhitelist(false);
      setShowPolicy(false);
      setShowAdvancedNetwork(false);
      setGameMode('all');
      onSuccess();
      onClose();
    } catch {
      // error handled by hook
    }
  };

  return (
    <CommonModal isOpen={isOpen} onClose={onClose} title="Add Domain" size="3xl" closeDisabled={createDomain.isLoading}>
      <CommonForm onSubmit={handleSubmit}>
            <FormField label="Domain" htmlFor="create-domain-host" required>
              <Input
                id="create-domain-host"
                required
                value={form.domain}
                onChange={(e) => setForm({ ...form, domain: e.target.value })}
                placeholder="kiosk1.example.com"
              />
            </FormField>

            <FormField label="Label" htmlFor="create-domain-label">
              <Input
                id="create-domain-label"
                value={form.label ?? ''}
                onChange={(e) => setForm({ ...form, label: e.target.value })}
                placeholder="Kiosk #1 - Main Hall"
              />
            </FormField>

            <FormGrid columns={2} align="end">
              <FormField label="Type" htmlFor="create-domain-type" required>
                <FormSelect
                  id="create-domain-type"
                  value={form.type}
                  onChange={(e) => setForm({ ...form, type: e.target.value as DomainType })}
                >
                  <option value="kiosk">Kiosk</option>
                  <option value="mobile">Mobile</option>
                  <option value="admin">Admin / Portal</option>
                </FormSelect>
              </FormField>

              <FormCheckboxField
                id="isActive"
                label="Active"
                checked={form.isActive ?? true}
                onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                className="sm:pb-1"
              />
            </FormGrid>

            <FormSection
              title="Game policy"
              description="Optional. Skip this section to use all games."
              action={
                <Button type="button" variant="outline" size="sm" onClick={() => setShowPolicy((v) => !v)}>
                  {showPolicy ? 'Hide' : 'Configure'}
                </Button>
              }
            >
              {!showPolicy ? (
                <p className="text-xs text-muted-foreground">
                  Effective policy: <span className="font-medium text-foreground">All games</span>.
                </p>
              ) : (
                <>
                  <FormField label="Game policy">
                    <div className={formChoiceRowClassName} role="radiogroup" aria-label="Game policy">
                      <label className={formChoiceLabelClassName}>
                        <input
                          type="radio"
                          checked={gameMode === 'all'}
                          onChange={() => {
                            setGameMode('all');
                            setForm((prev) => ({ ...prev, game_ids: [] }));
                          }}
                        />
                        <span>All games (default)</span>
                      </label>
                      <label className={formChoiceLabelClassName}>
                        <input
                          type="radio"
                          checked={gameMode === 'selected'}
                          onChange={() => setGameMode('selected')}
                        />
                        <span>Selected games only</span>
                      </label>
                    </div>
                    {gameMode === 'selected' && (
                      <div className="max-h-48 overflow-y-auto border rounded-md p-2 space-y-2">
                        {(gameProvidersData?.data?.items || []).map((gp) => {
                          const checked = (form.game_ids || []).includes(gp.id);
                          return (
                            <label key={gp.id} className="flex items-center gap-2 text-sm">
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={(e) => {
                                  const current = form.game_ids || [];
                                  const game_ids = e.target.checked
                                    ? [...current, gp.id]
                                    : current.filter((id) => id !== gp.id);
                                  setForm({ ...form, game_ids });
                                }}
                              />
                              <span>{gp.name} ({gp.code})</span>
                            </label>
                          );
                        })}
                        {(gameProvidersData?.data?.items || []).length === 0 && (
                          <p className="text-xs text-muted-foreground">No game providers found.</p>
                        )}
                      </div>
                    )}
                    {gameMode === 'selected' && (form.game_ids?.length || 0) === 0 && (
                      <p className="text-xs text-amber-700">
                        No game selected. This will fallback to all games.
                      </p>
                    )}
                  </FormField>
                </>
              )}
            </FormSection>

            {form.type === 'kiosk' && (
              <FormSection
                title="Network restrictions"
                action={
                  <Button type="button" variant="outline" size="sm" onClick={() => setShowAdvancedNetwork((v) => !v)}>
                    {showAdvancedNetwork ? 'Hide' : 'Configure'}
                  </Button>
                }
              >
                {showAdvancedNetwork && (
                  <>
                    <FormCheckboxField
                      id="useWhitelist"
                      label="Enable IP Whitelist"
                      checked={useWhitelist}
                      onChange={(e) => handleToggleWhitelist(e.target.checked)}
                    />
                    {!useWhitelist && (
                      <p className="text-xs text-muted-foreground">All IPs are allowed when whitelist is disabled.</p>
                    )}
                    {useWhitelist && (
                      <>
                        <div className="flex gap-2">
                          <Input
                            value={ipInput}
                            onChange={(e) => setIpInput(e.target.value)}
                            placeholder="192.168.1.100"
                            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddIp(); } }}
                          />
                          <Button type="button" variant="outline" size="sm" onClick={handleAddIp}>
                            <Plus className="h-4 w-4" />
                          </Button>
                        </div>
                        <div className="space-y-1">
                          {(form.ipWhitelist ?? []).map((ip) => (
                            <div key={ip} className="flex items-center justify-between bg-accent/50 px-3 py-1.5 rounded text-sm font-mono">
                              <span>{ip}</span>
                              <button type="button" onClick={() => handleRemoveIp(ip)} className="text-destructive hover:text-destructive/80">
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          ))}
                          {(form.ipWhitelist ?? []).length === 0 && (
                            <p className="text-xs text-muted-foreground">No IPs added — all requests will be blocked.</p>
                          )}
                        </div>
                      </>
                    )}
                  </>
                )}
              </FormSection>
            )}

            <FormSection
              title="Cloudflare DNS (optional)"
              description="If set, a CNAME record pointing this domain to the target will be created automatically in Cloudflare. Leave blank to manage DNS manually."
            >
              <Input
                id="create-domain-cf-cname"
                value={form.cfCnameTarget ?? ''}
                onChange={(e) => setForm({ ...form, cfCnameTarget: e.target.value })}
                placeholder="kiosk-fe.pages.dev"
              />
            </FormSection>

            <FormActions>
              <Button type="button" variant="outline" onClick={onClose} disabled={createDomain.isLoading}>
                Cancel
              </Button>
              <Button type="submit" disabled={createDomain.isLoading}>
                {createDomain.isLoading ? 'Creating...' : 'Create Domain'}
              </Button>
            </FormActions>
      </CommonForm>
    </CommonModal>
  );
};

export default CreateDomainModal;
